import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useProposeBenefitOverride } from '@/lib/disruptions/use-benefit-overrides';
import type { DisruptionPnrGroupRow } from '@/lib/disruptions/types';

// "Elevar monto" (#819, SKY PoC A9) — proposes a NEW per-passenger meal value for
// THIS event only. The policy is never modified: the dialog shows what the policy
// proposed (read off the roster) against the new value, asks for a reason, and
// creates a PROPOSED record that a DIFFERENT person with an authorizing role must
// approve before anything is applied. Nothing moves on submit.

/** The policy-proposed per-passenger value the roster already carries (modal). */
export function policyProposedOf(rows: DisruptionPnrGroupRow[]): {
  amountUsd: number;
  currency: string;
  policyUrn?: string;
} | null {
  const counts = new Map<string, number>();
  const policies = new Map<string, number>();
  for (const r of rows) {
    const mv = r.mealVoucher;
    if (!mv || typeof mv.amountUsd !== 'number') continue;
    const key = `${(mv.currency ?? 'USD').toUpperCase()}|${mv.amountUsd}`;
    counts.set(key, (counts.get(key) ?? 0) + 1);
    if (r.governingPolicyUrn) policies.set(r.governingPolicyUrn, (policies.get(r.governingPolicyUrn) ?? 0) + 1);
  }
  const top = [...counts.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))[0];
  if (!top) return null;
  const [currency, amount] = top[0].split('|');
  const policyUrn = [...policies.entries()].sort((a, b) => b[1] - a[1])[0]?.[0];
  return { amountUsd: Number(amount), currency: currency ?? 'USD', ...(policyUrn ? { policyUrn } : {}) };
}

export function CareBenefitOverrideDialog({
  disruptionUrn,
  rows,
  onClose,
}: {
  disruptionUrn: string;
  rows: DisruptionPnrGroupRow[];
  onClose: () => void;
}) {
  const { t } = useTranslation('care');
  const proposed = policyProposedOf(rows);
  const currency = proposed?.currency ?? 'USD';
  const [amountRaw, setAmountRaw] = useState('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const propose = useProposeBenefitOverride(disruptionUrn);

  const amount = Number(amountRaw);
  const amountOk = Number.isFinite(amount) && amount > 0;
  const increases = proposed ? amount > proposed.amountUsd : true;
  const canSubmit = amountOk && reason.trim().length > 0 && !propose.isPending;

  const submit = async (): Promise<void> => {
    if (!canSubmit) return;
    setError(null);
    try {
      await propose.mutateAsync({
        amountUsd: amount,
        currency,
        reason: reason.trim(),
        ...(proposed ? { proposedByPolicy: { amountUsd: proposed.amountUsd, currency } } : {}),
        ...(proposed?.policyUrn ? { policyUrn: proposed.policyUrn } : {}),
      });
      onClose();
    } catch (err) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      setError(status === 409 ? t('override.dialog.errors.noPolicyValue') : t('override.dialog.errors.generic'));
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-lg" data-testid="benefit-override-dialog">
        <DialogHeader>
          <DialogTitle>{t('override.dialog.title')}</DialogTitle>
          <DialogDescription>{t('override.dialog.subtitle')}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3 rounded-md border border-border bg-muted/30 p-3 text-sm">
            <div>
              <p className="text-xs text-muted-foreground">{t('override.dialog.policyValue')}</p>
              <p className="text-lg font-semibold tabular-nums" data-testid="benefit-override-policy-value">
                {proposed ? `${proposed.amountUsd} ${currency}` : t('override.dialog.policyValueUnknown')}
              </p>
              {proposed?.policyUrn ? (
                <p className="truncate text-[11px] text-muted-foreground" title={proposed.policyUrn}>
                  {t('override.dialog.policyRef', { urn: proposed.policyUrn.split(':').pop() })}
                </p>
              ) : null}
            </div>
            <div>
              <label htmlFor="benefit-override-amount" className="text-xs text-muted-foreground">
                {t('override.dialog.newValue')}
              </label>
              <div className="flex items-center gap-1">
                <input
                  id="benefit-override-amount"
                  type="number"
                  inputMode="decimal"
                  min={0}
                  step={1}
                  value={amountRaw}
                  onChange={(e) => setAmountRaw(e.target.value)}
                  className="w-24 rounded-md border border-input bg-background px-2 py-1 text-lg font-semibold tabular-nums"
                  data-testid="benefit-override-amount"
                  aria-label={t('override.dialog.newValueAria')}
                />
                <span className="text-sm text-muted-foreground">{currency}</span>
              </div>
            </div>
          </div>
          <p className="text-xs text-muted-foreground">{t('override.dialog.perPax')}</p>
          {amountOk && !increases ? (
            <p className="rounded-md border border-amber-300 bg-amber-50 p-2 text-xs text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200" data-testid="benefit-override-no-increase">
              {t('override.dialog.noIncrease')}
            </p>
          ) : null}

          <div>
            <label htmlFor="benefit-override-reason" className="text-xs font-medium">
              {t('override.dialog.reason')}
            </label>
            <textarea
              id="benefit-override-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              placeholder={t('override.dialog.reasonPlaceholder')}
              className="mt-1 w-full rounded-md border border-input bg-background px-2 py-1.5 text-sm"
              data-testid="benefit-override-reason"
            />
            <p className="mt-1 text-[11px] text-muted-foreground">{t('override.dialog.reasonHint')}</p>
          </div>

          <p className="rounded-md bg-muted/50 p-2 text-xs text-muted-foreground" data-testid="benefit-override-policy-note">
            {t('override.dialog.policyUntouched')}
          </p>

          {error ? (
            <p role="alert" className="text-sm text-rose-700 dark:text-rose-300" data-testid="benefit-override-error">
              {error}
            </p>
          ) : null}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={propose.isPending}>
            {t('override.dialog.cancel')}
          </Button>
          <Button onClick={() => void submit()} disabled={!canSubmit} data-testid="benefit-override-submit">
            {propose.isPending ? <Loader2 className="mr-1 h-4 w-4 animate-spin" aria-hidden /> : null}
            {t('override.dialog.submit')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
